import { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";

import DstButton from "../../components/DstButton/DstButton";
import Lister from "../../components/Lister/Lister";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import { useSession } from "../../contexts/SessionContext";
import { Ephemeral } from "../../models/Ephemeral";
import { PermissionComponent } from "../../services/PermissionGuard";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { fetchEphemerals } from "./Ephemerals.function";
import EphemeralCreationModal from "./components/CreationModal";

export const Ephemerals = () => {
  const { language } = useSession();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [ephemerals, setEphemerals] = useState<Ephemeral[]>([]);
  const [onlyActive, setOnlyActive] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [showModalCreation, setShowModalCreation] = useState(false);

  useEffect(() => {
    fetchEphemerals(setEphemerals, setLoaded, language);
  }, [language]);

  const _filteredItems = useMemo(
    () =>
      ephemerals.filter(
        (item) =>
          (!onlyActive || !item.is_deprecated) &&
          Object.values(item).join(" ").toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [ephemerals, searchQuery, onlyActive]
  );

  const columns = [
    { header: translate(language, "PAGES.EPHEMERAL.ID"), key: "ID", sortType: "number" },
    { header: translate(language, "PAGES.EPHEMERAL.NAME"), key: "name", sortType: "string" },
    { header: translate(language, "PAGES.EPHEMERAL.TITLE_COLUMN"), key: "title", sortType: "string" },
    { header: translate(language, "PAGES.EPHEMERAL.CURSUS"), key: "cursus", sortType: "string" },
    {
      header: translate(language, "PAGES.EPHEMERAL.MANDATORY_RATIO"),
      key: "mandatory_ratio",
      sortType: "number",
      render: (item: Ephemeral) => <span>{item.mandatory_ratio}</span>,
    },
    {
      header: translate(language, "PAGES.EPHEMERAL.ACTIVE"),
      key: "is_deprecated",
      render: (item: Ephemeral) => (
        <div className="text-center">
          {!item.is_deprecated ? (
            <span className="dst-icon-check green-font" />
          ) : (
            <span className="dst-icon-x dark-red-font" />
          )}
        </div>
      ),
    },
    {
      header: translate(language, "PAGES.EPHEMERAL.CREATED_DATE"),
      key: "dt_created",
      sortType: "date",
      render: (item: Ephemeral) => (
        <span title={formatDateHumanly(language, new Date(item.dt_created * 1000).toISOString(), true)}>
          {formatDateHumanly(language, new Date(item.dt_created * 1000).toISOString())}
        </span>
      ),
    },
  ];

  return (
    <div className="m-3">
      <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
        <div className="d-flex flex-wrap gap-3 align-items-center">
          <h2 className="mb-0 me-3">{translate(language, "PAGES.EPHEMERAL.TITLE")}</h2>
          <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
          <DstButton
            btnClass="h-100 d-flex align-items-center me-2"
            btnImageBefore={
              <Form.Check
                type="switch"
                className="check-box s-bold me-2"
                checked={onlyActive}
                readOnly
                aria-label={translate(language, "PAGES.EPHEMERAL.ONLY_ACTIVE")}
              />
            }
            value={translate(language, "PAGES.EPHEMERAL.ONLY_ACTIVE")}
            variant="light"
            btnHeight="50px"
            btnWidth="200px"
            clickFunction={() => setOnlyActive(!onlyActive)}
          />
        </div>
        <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/ephemeral-evaluations" }]}>
          <DstButton
            value={translate(language, "PAGES.EPHEMERAL.BUTTON_ADD_SESSION")}
            btnImageBefore={<span className="me-2 dst-icon-plus" />}
            clickFunction={() => {
              setShowModalCreation(true);
            }}
          />
        </PermissionComponent>
      </div>
      <Lister
        key={`${searchQuery}-${onlyActive}`}
        columns={columns}
        items={_filteredItems}
        mock={!loaded}
        pagination
        paginationAlwaysShowFooter
        itemsPerPage={50}
      />
      <EphemeralCreationModal
        language={language}
        setShowCreationModal={setShowModalCreation}
        showCreationModal={showModalCreation}
        onCreated={() => fetchEphemerals(setEphemerals, setLoaded, language)}
      />
    </div>
  );
};

export default Ephemerals;
